import { useCallback } from 'react'
import { useReserveNavigate } from '~/navigation'
import { sva } from '~/styled-system/css'
import { Button } from '~/ui'
import { useSelectedOptionMenusWithDetails } from '../store/optionMenu'
import type { SelectedOptionMenuWithDetails } from '../store/optionMenu'
import { useOptionMenus } from '../store/stateMachine/state'

const styles = sva({
  slots: ['container', 'heading', 'title', 'content'],
  base: {
    container: {
      spaceY: '2',
      borderTop: 'light',
      paddingTop: '3',
      md: {
        display: 'flex',
        alignItems: 'start',
        spaceY: '0',
        spaceX: '3',
      },
    },
    heading: {
      spaceY: '3',
      md: {
        spaceY: '0',
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'space-between',
      },
    },
    title: {
      width: '[100px]',
      fontSize: 'sm',
      fontWeight: 'bold',
      md: {
        fontSize: 'md',
        flexShrink: '0',
      },
    },
    content: {
      rounded: 'default',
      backgroundColor: 'gray.100',
      padding: '3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      spaceX: '3',
      width: 'full',
      fontSize: 'sm',
    },
  },
})

export function OptionMenu() {
  const { hasOptions, onClick, selectedOptions } = useOptionMenu()
  const classes = styles()
  if (!hasOptions) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <h3 className={classes.title}>オプション</h3>
      </div>
      <div className={classes.content}>
        {selectedOptions.length === 0 && (
          <>
            <p>オプションの追加が可能です</p>
            <Button color="tertiary" size="xs" onClick={onClick}>
              追加
            </Button>
          </>
        )}
        <SelectedOptionMenus options={selectedOptions} />
      </div>
    </div>
  )
}

function useOptionMenu() {
  const hasOptions = useOptionMenus().length > 0
  const selectedOptions = useSelectedOptionMenusWithDetails()
  const navigate = useReserveNavigate()
  const onClick = useCallback(() => {
    navigate({ type: 'showOption' })
  }, [navigate])
  return { hasOptions, onClick, selectedOptions }
}

const optionMenuStyles = sva({
  slots: ['container', 'content', 'item', 'name', 'count'],
  base: {
    container: {
      display: 'flex',
      spaceX: '3',
      width: 'full',
    },
    content: {
      width: 'full',
      flex: '1',
      spaceY: '2',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      fontSize: 'sm',
    },
    item: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      borderTop: 'dotted',
      paddingTop: '2',
      fontSize: 'sm',
      _first: {
        borderTop: 'none',
        padding: '0',
      },
    },
    name: {
      flex: '1',
      fontSize: 'sm',
    },
    count: {
      width: '[40px]',
      textAlign: 'right',
    },
  },
})

function SelectedOptionMenus({ options }: { options: SelectedOptionMenuWithDetails[] }) {
  const { onClick } = useOptionMenu()
  const classes = optionMenuStyles()
  if (options.length === 0) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.content}>
        {options.map(({ optionMenuId, name, count }) => {
          return (
            <div className={classes.item} key={optionMenuId}>
              <p className={classes.name}>{name}</p>
              <p className={classes.count}>{count}点</p>
            </div>
          )
        })}
      </div>
      <div>
        <Button color="tertiary" size="xs" onClick={onClick}>
          変更
        </Button>
      </div>
    </div>
  )
}
